import { FC } from "react";
import { Box, Flex, FlexProps } from "@otrium/core";
import {
  Promotion,
  PromotionDisplayType,
  PromotionType,
  ShopType,
  WelcomePopup,
} from "src/types/graphql.d";
import { Trans } from "@lingui/macro";
import { Discount } from "src/icons/Discount";
import styled from "@emotion/styled";
import useAudienceSpecificPromo, {
  AudienceSpecificPromoData,
} from "src/hooks/useAudienceSpecificPromo";

const WelcomePromoText = (promotion: WelcomePopup): JSX.Element | null => {
  return promotion?.subTitle ? (
    <Box color="tone.black" sx={{ flex: 1 }}>
      {`${promotion.subTitle} `}
    </Box>
  ) : null;
};

const OngoingPromoText = (promotion: Promotion): JSX.Element | null => {
  return promotion.promo_message ? (
    <>
      {!promotion?.promo_code &&
        (promotion?.promo_message_url ? (
          <Box
            as="a"
            href={promotion?.promo_message_url}
            color="tone.black"
            sx={{ flex: 1 }}
          >
            {`${promotion.promo_message} `}
          </Box>
        ) : (
          <Box color="tone.black" sx={{ flex: 1 }}>
            {`${promotion.promo_message} `}
          </Box>
        ))}

      {promotion?.promo_code && (
        <Box color="tone.black" sx={{ flex: 1 }}>
          {`${promotion.promo_message} `}
          <Trans>Promo code:</Trans>{" "}
          <StyledPromoCode>{promotion.promo_code}</StyledPromoCode>
        </Box>
      )}
    </>
  ) : null;
};

export const PromoTextContent: FC<{
  promotion: AudienceSpecificPromoData;
}> = ({ promotion }): JSX.Element | null => {
  return "promo_message" in promotion || "subTitle" in promotion ? (
    <StyledPromotionBanner data-testid="promo-text">
      <Box height="24px" width="35px" sx={{ flex: "0 0 35px" }}>
        <Discount width="20px" />
      </Box>
      {"promo_message" in promotion && <OngoingPromoText {...promotion} />}
      {"subTitle" in promotion && <WelcomePromoText {...promotion} />}
    </StyledPromotionBanner>
  ) : null;
};

type Props = FlexProps & {
  slug: string;
  shopType: ShopType;
  promotionType: PromotionType;
};
const PromoText = ({
  slug,
  shopType,
  promotionType,
}: Props): JSX.Element | null => {
  const { data } = useAudienceSpecificPromo({
    slug,
    category: promotionType,
    shopType,
  });

  return data &&
    (("promo_message" in data && data.promo_message) ||
      ("subTitle" in data && data.subTitle)) &&
    "promo_display_type" in data &&
    data.promo_display_type !== PromotionDisplayType.FlashSale ? (
    <PromoTextContent promotion={data} />
  ) : null;
};

export default PromoText;

const StyledPromotionBanner = styled(Flex)`
  background-color: ${({ theme }) => theme.colors.tone.sand};
  padding: ${({ theme }) => theme.space[2]}px ${({ theme }) => theme.space[3]}px;
  border-radius: ${({ theme }) => theme.space[2]}px;
  align-items: center;
  font-size: ${({ theme }) => theme.fontSizes[0]}px;
`;

const StyledPromoCode = styled.span`
  font-weight: bold;
`;
